import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useOnboarding } from '../../../context/OnboardingContext';
import { stripeGlobal, logPurchaseCompletedSuccess } from './StripeHelper';
import { usePaymentStore, paymentStore } from './StripeHelper';
import { getPlanPrice } from '../../../config/plans';
import { trackPurchaseStarted, trackInitiateCheckout } from '../../../services/analytics';
import { createStripeSubscriptionWallet } from '../../../services/api';
import AccordionPanel from './shared/AccordionPanel';

const StripeApplePay = forwardRef(({ isExpanded, onSelect }, ref) => {
    const { userID, selectedPlan, onboardingId, formData, globalAnswers, goToNextScreen, urlParams } = useOnboarding();
    const [paymentError, setPaymentError] = useState(null);

    const pm = usePaymentStore();

    const [isProcessing, setIsProcessing] = useState(pm.isProcessing);

    useEffect(() => {
        setIsProcessing(pm.isProcessing);
    }, [pm]);

    let paymentRequest = null;
    const stripeElementsRef = useRef(null);
    let email = formData.get('email') ?? null;
    
    const createPaymentRequest = () => {

        console.log('apple pay: selected plan', selectedPlan);

        if (!email) {
            email = prompt("Please enter your email");
        }

        const price = getPlanPrice(selectedPlan?.id);

        const pr = stripeGlobal.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: {
                label: selectedPlan.title,
                amount: price,
            },
            requestPayerName: false,
            requestPayerEmail: false,
        });


        pr.on('paymentmethod', async (event) => {

            paymentStore.setState({ isProcessing: true });
            setIsProcessing(true);
            setPaymentError(null);

            try {
                const params = {
                    userID: userID,
                    email: event.payerEmail || email,
                    name: event.payerName,
                    priceID: selectedPlan?.priceID,
                    discountID: selectedPlan?.discountID,
                    paymentMethodId: event.paymentMethod.id,
                    onboardingID: onboardingId,
                    goal: globalAnswers.get('goal')?.value || "unknown",
                    voice: globalAnswers.get('voice')?.value || "unknown",
                    queryParams: urlParams || {}
                }

                const response = await createStripeSubscriptionWallet("/stripe.createSubscriptionApplePay.php", params);

                event.complete('success');

                const subscription = await response.json();

                const customerID = subscription.customer ?? null;
                if (customerID) {
                    console.log('save customerID', customerID);
                    localStorage.setItem('customerID', customerID);
                    localStorage.setItem('paymentProvider', 'stripe');
                }
                else {
                    console.log('no customerID', subscription, subscription.customer);
                }

                console.log('subscription', subscription);

                // Проверяем результат создания подписки
                if (subscription.error) {
                    // Показываем ошибку пользователю
                    console.error('Ошибка создания подписки:', subscription.error);
                    // Здесь можно обновить UI с сообщением об ошибке
                    paymentStore.setState({ isProcessing: false });
                    return;
                }

                if (subscription.status === 'active') {

                    logPurchaseCompletedSuccess(onboardingId, selectedPlan?.priceID, selectedPlan?.discountID, price, urlParams, email);

                    console.log('subscription active');
                    paymentStore.setState({ isProcessing: false });
                    goToNextScreen();

                } else if (subscription.status === 'incomplete' &&
                    subscription.latest_invoice?.confirmation_secret) {
                    // Нужна дополнительная аутентификация (3D Secure)

                    console.log('subscription incomplete');

                    const { client_secret } = subscription.latest_invoice.confirmation_secret;

                    console.log('client_secret', client_secret);

                    const { error, paymentIntent } = await stripeGlobal.confirmCardPayment(
                        client_secret
                    );

                    if (error) {
                        // Ошибка при подтверждении платежа
                        console.error('Ошибка подтверждения:', error);
                        // Обновляем UI с сообщением об ошибке
                    } else if (paymentIntent.status === 'succeeded') {
                        console.log('subscription active 2');

                        logPurchaseCompletedSuccess(onboardingId, selectedPlan?.priceID, selectedPlan?.discountID, price, urlParams, email);

                        paymentStore.setState({ isProcessing: false });
                        goToNextScreen();
                    } else {
                        // Другой статус PaymentIntent
                        console.log('Статус платежа:', paymentIntent.status);
                    }
                } else {
                    console.log('Статус подписки:', subscription.status);
                }
            } catch (error) {
                console.error('Payment error:', error);
                event.complete('fail');
                paymentStore.setState({ isProcessing: false });
                setPaymentError(error.message || 'Payment failed');
            } finally {
                paymentStore.setState({ isProcessing: false });
            }

        });

        pr.on('paymentfailed', async (event) => {
            console.log('Payment failed', event);
            setPaymentError(event.error.message || 'Payment failed');
            paymentStore.setState({ isProcessing: false });
        });

        pr.on('paymentauthorized', async (event) => {
            console.log('Payment authorized', event);
            setIsProcessing(true);
            paymentStore.setState({ isProcessing: false });
        });

        pr.on('cancel', async (event) => {
            console.log('google pay: Payment canceled', event);
            setIsProcessing(false);
            paymentStore.setState({ isProcessing: false });
        });

        return pr;
    };

    const handleApplePayClick = async () => {

        paymentRequest = createPaymentRequest();

        const result = await paymentRequest.canMakePayment();

        if (!result || !result.applePay) {
            console.log('apple pay: Apple Pay is not available');
            paymentStore.setState({ isProcessing: true });
            return;
        }

        paymentStore.setState({ isProcessing: true });
        setIsProcessing(true);

        try {
            trackPurchaseStarted({
                type: 'stripeApplePay',
                onboardingId,
                priceID: selectedPlan?.priceID,
                discountID: selectedPlan?.discountID,
            });
        } catch (error) {
            console.error('Error tracking purchase started:', error);
        }

        try {
            trackInitiateCheckout(formData.get('email') ?? "unknown");
        } catch (error) {
            console.error('Error logging initiate checkout:', error);
        }

        try {
            setPaymentError(null);
            paymentRequest.show();
        } catch (error) {
            console.error('Error showing Apple Pay:', error);
            paymentStore.setState({ isProcessing: false });
            setPaymentError('Failed to show Apple Pay. Please try again.');
        }
    };

    // Expose handleApplePayClick to parent component via ref
    useImperativeHandle(ref, () => ({
        handleApplePayClick
    }));

    return (
        <section
            className="item"
            data-state={isExpanded ? 'expanded' : 'collapsed'}
            onClick={onSelect}
        >
            <button type="button" className="trigger" aria-expanded={isExpanded}>
                <div className="radio-circle"></div>
                <div className="payment-icons">
                    <div className="image-wrap svelte-grrljh">
                        <svg className="payment-method-icon svelte-171041c" height="26" viewBox="0 0 63 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.41022 4.82398C9.5916 4.92293 10.773 4.23026 11.5113 3.35205C12.2374 2.4491 12.7173 1.23692 12.5943 0C11.5483 0.0494767 10.2561 0.692674 9.51777 1.59562C8.84093 2.37488 8.26255 3.63654 8.41022 4.82398ZM22.4638 20.7555V1.47193H29.6628C33.3793 1.47193 35.9758 4.04471 35.9758 7.80494C35.9758 11.5652 33.33 14.1627 29.5644 14.1627H25.4418V20.7555H22.4638ZM12.5819 5.05898C11.5411 4.99877 10.5914 5.37358 9.82438 5.67633C9.33075 5.87116 8.91274 6.03614 8.59472 6.03614C8.23784 6.03614 7.80257 5.86234 7.31387 5.66719C6.6735 5.4115 5.94138 5.11916 5.17364 5.13319C3.41387 5.15793 1.77716 6.15983 0.878819 7.75545C-0.967091 10.9467 0.398882 15.6717 2.18326 18.2693C3.05699 19.5556 4.10301 20.9657 5.48129 20.9163C6.08765 20.8933 6.52383 20.7072 6.97524 20.5147C7.49492 20.293 8.0348 20.0628 8.87776 20.0628C9.69151 20.0628 10.2078 20.287 10.7033 20.5023C11.1746 20.707 11.6271 20.9036 12.2989 20.8915C13.7264 20.8668 14.6247 19.6051 15.4984 18.3187C16.4413 16.9381 16.8557 15.5906 16.9186 15.3861C16.9222 15.3745 16.9246 15.3665 16.9259 15.3625C16.9244 15.361 16.9128 15.3556 16.8922 15.3462C16.577 15.2011 14.1679 14.0926 14.1448 11.1199C14.1216 8.62473 16.0556 7.36054 16.3601 7.16153C16.3786 7.14944 16.3911 7.14125 16.3968 7.137C15.1662 5.30636 13.2464 5.10845 12.5819 5.05898ZM41.4153 20.9039C43.2858 20.9039 45.0209 19.9515 45.8085 18.4424H45.8701V20.7555H48.6266V11.157C48.6266 8.37393 46.4115 6.5804 43.0027 6.5804C39.8401 6.5804 37.5019 8.39866 37.4158 10.8972H40.0985C40.32 9.70979 41.4153 8.93054 42.9166 8.93054C44.7379 8.93054 45.7593 9.78401 45.7593 11.3549V12.4186L42.0429 12.6413C38.5849 12.8516 36.7143 14.274 36.7143 16.7479C36.7143 19.2464 38.6464 20.9039 41.4153 20.9039ZM42.215 18.6156C40.6275 18.6156 39.6184 17.8487 39.6184 16.6736C39.6184 15.4615 40.5906 14.7564 42.4488 14.6451L45.7591 14.4348V15.5233C45.7591 17.3292 44.2332 18.6156 42.215 18.6156ZM57.7699 21.51C56.5762 24.8868 55.2103 26 52.306 26C52.0845 26 51.3461 25.9753 51.1739 25.9258V23.6127C51.3585 23.6375 51.8138 23.6622 52.0476 23.6622C53.3643 23.6622 54.1027 23.1056 54.558 21.6584L54.8288 20.8049L49.7833 6.76594H52.8967L56.4039 18.1579H56.4655L59.9727 6.76594H63L57.7699 21.51ZM25.4416 3.99524H28.875C31.4592 3.99524 32.936 5.38059 32.936 7.81731C32.936 10.254 31.4592 11.6518 28.8627 11.6518H25.4416V3.99524Z" fill="currentColor" />
                        </svg>
                    </div>
                </div>
            </button>
            <AccordionPanel expanded={isExpanded}>
                <div className="panel" role="region">
                    <div className="content svelte-rwsxt0" style={{ height: 'auto' }}>
                        <div className="content svelte-grrljh">
                            <div className="svelte-1el77kq">
                                <div className="bullets svelte-1op0a8f">
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Easy and private payments with Face/Touch ID</span>
                                    </li>
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Keeps your financial info safe with end-to-end encryption</span>
                                    </li>
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Protected by Apple Pay's unique Device Account Number</span>
                                    </li>
                                </div>
                                <div data-testid="stripe-wallet-checkout" className="container svelte-ykx1g">
                                    <button
                                        className="svelte-1wws8ka"
                                        type="button"
                                        onClick={handleApplePayClick}
                                        disabled={isProcessing}
                                    >
                                        <div className="svelte-lss1u3">
                                            {isProcessing ? (
                                                <span className="button-spinner">
                                                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                                                            <animate attributeName="stroke-dasharray" values="1,150;90,150;1,150" dur="1.5s" repeatCount="indefinite" />
                                                            <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite" />
                                                        </circle>
                                                    </svg>
                                                </span>
                                            ) : (
                                                <>
                                                    Continue with{' '}
                                                    <svg className="payment-method-icon svelte-lss1u3" height="26" viewBox="0 0 63 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M8.41022 4.82398C9.5916 4.92293 10.773 4.23026 11.5113 3.35205C12.2374 2.4491 12.7173 1.23692 12.5943 0C11.5483 0.0494767 10.2561 0.692674 9.51777 1.59562C8.84093 2.37488 8.26255 3.63654 8.41022 4.82398ZM22.4638 20.7555V1.47193H29.6628C33.3793 1.47193 35.9758 4.04471 35.9758 7.80494C35.9758 11.5652 33.33 14.1627 29.5644 14.1627H25.4418V20.7555H22.4638ZM12.5819 5.05898C11.5411 4.99877 10.5914 5.37358 9.82438 5.67633C9.33075 5.87116 8.91274 6.03614 8.59472 6.03614C8.23784 6.03614 7.80257 5.86234 7.31387 5.66719C6.6735 5.4115 5.94138 5.11916 5.17364 5.13319C3.41387 5.15793 1.77716 6.15983 0.878819 7.75545C-0.967091 10.9467 0.398882 15.6717 2.18326 18.2693C3.05699 19.5556 4.10301 20.9657 5.48129 20.9163C6.08765 20.8933 6.52383 20.7072 6.97524 20.5147C7.49492 20.293 8.0348 20.0628 8.87776 20.0628C9.69151 20.0628 10.2078 20.287 10.7033 20.5023C11.1746 20.707 11.6271 20.9036 12.2989 20.8915C13.7264 20.8668 14.6247 19.6051 15.4984 18.3187C16.4413 16.9381 16.8557 15.5906 16.9186 15.3861C16.9222 15.3745 16.9246 15.3665 16.9259 15.3625C16.9244 15.361 16.9128 15.3556 16.8922 15.3462C16.577 15.2011 14.1679 14.0926 14.1448 11.1199C14.1216 8.62473 16.0556 7.36054 16.3601 7.16153C16.3786 7.14944 16.3911 7.14125 16.3968 7.137C15.1662 5.30636 13.2464 5.10845 12.5819 5.05898ZM41.4153 20.9039C43.2858 20.9039 45.0209 19.9515 45.8085 18.4424H45.8701V20.7555H48.6266V11.157C48.6266 8.37393 46.4115 6.5804 43.0027 6.5804C39.8401 6.5804 37.5019 8.39866 37.4158 10.8972H40.0985C40.32 9.70979 41.4153 8.93054 42.9166 8.93054C44.7379 8.93054 45.7593 9.78401 45.7593 11.3549V12.4186L42.0429 12.6413C38.5849 12.8516 36.7143 14.274 36.7143 16.7479C36.7143 19.2464 38.6464 20.9039 41.4153 20.9039ZM42.215 18.6156C40.6275 18.6156 39.6184 17.8487 39.6184 16.6736C39.6184 15.4615 40.5906 14.7564 42.4488 14.6451L45.7591 14.4348V15.5233C45.7591 17.3292 44.2332 18.6156 42.215 18.6156ZM57.7699 21.51C56.5762 24.8868 55.2103 26 52.306 26C52.0845 26 51.3461 25.9753 51.1739 25.9258V23.6127C51.3585 23.6375 51.8138 23.6622 52.0476 23.6622C53.3643 23.6622 54.1027 23.1056 54.558 21.6584L54.8288 20.8049L49.7833 6.76594H52.8967L56.4039 18.1579H56.4655L59.9727 6.76594H63L57.7699 21.51ZM25.4416 3.99524H28.875C31.4592 3.99524 32.936 5.38059 32.936 7.81731C32.936 10.254 31.4592 11.6518 28.8627 11.6518H25.4416V3.99524Z" fill="currentColor" />
                                                    </svg>
                                                </>
                                            )}
                                        </div>
                                    </button>
                                </div>
                                {paymentError && (
                                    <div style={{
                                        color: '#ff6b6b',
                                        fontSize: '14px',
                                        marginTop: '12px',
                                        textAlign: 'center'
                                    }}>
                                        {paymentError}
                                    </div>
                                )}
                                <div className="footer svelte-p3ln32">
                                    <span className="svelte-kfp6vj">Your payment information is secure with SSL/TLS encryption</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AccordionPanel>
        </section>
    );
});

StripeApplePay.displayName = 'StripeApplePay';

export default StripeApplePay;

